import { useSelector } from 'react-redux';
import { apiSlice } from './apiSlice';
import { selectIsAuthenticated } from '../slices/authSlice';
import { t } from '../../i18n/i18n';

/**
 * Pre-launch sign-ups — the "notify me" forms on Alpha DNA, Alpha OPS and
 * Divento.
 *
 *   POST /product-launch/subscribe/ ← { product, email }
 *     product: 'alpha_dna' | 'alpha_ops' | 'divento'
 *     201 the first sign-up, 200 the saved email replaced — both mean the
 *     trader is on the list. 400 carries field messages ({ email: [...] }),
 *     429 comes after ten requests in an hour.
 *
 *   GET /product-launch/subscriptions/ → the trader's sign-ups, one row per
 *     product: [{ product, email, created_at, updated_at, … }] — so a page can
 *     say "already on the list" after a reload.
 *
 * Sending again is safe and only replaces the email.
 */
export const productLaunchApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProductLaunchSubscriptions: builder.query({
      query: () => '/product-launch/subscriptions/',
      providesTags: ['ProductLaunch'],
    }),

    subscribeProductLaunch: builder.mutation({
      query: ({ product, email }) => ({
        url: '/product-launch/subscribe/',
        method: 'POST',
        body: { product, email },
      }),
      invalidatesTags: ['ProductLaunch'],
    }),
  }),
});

export const { useGetProductLaunchSubscriptionsQuery, useSubscribeProductLaunchMutation } =
  productLaunchApi;

/** One product's row out of the subscriptions list, or null. */
export const subscriptionFor = (data, product) =>
  (Array.isArray(data) ? data : []).find((row) => row?.product === product) ?? null;

/**
 * Whether the signed-in trader is already on a product's list. The pages are
 * public, so a signed-out visitor isn't asked about — the form shows as usual.
 */
export const useLaunchSubscription = (product) => {
  const signedIn = useSelector(selectIsAuthenticated);
  const { data } = useGetProductLaunchSubscriptionsQuery(undefined, { skip: !signedIn });
  return subscriptionFor(data, product);
};

/** What to tell the trader when a sign-up fails, from the error RTK Query hands back. */
export const subscribeErrorMessage = (error) => {
  const field = error?.data?.email?.[0] ?? error?.data?.product?.[0];
  if (error?.status === 400 && field) return t(field);
  if (error?.status === 429) return t('Too many attempts. Try again in a few minutes.');
  if (error?.status === 401) return t('Your session has expired. Please sign in again.');
  return t('Something went wrong. Please try again.');
};
